import { useLayoutEffect, useMemo, useState } from 'react';

import { useSelector } from 'react-redux';

import { ConferenceStatus } from '../../../../../pages/call/constants';
import { isPermissionsChangeAllowedSelector } from '../../../../../pages/call/features/permissions';
import {
    getConferenceStatus,
    selectIsPermissionsExtendedActive,
    selectIsPermissionsExtendedEnabled,
} from '../../../../../pages/call/selectors';
import { supportsVirtualBackground } from '../../../../helpers/browser-capabilities';
import { isMessengerIntegration as selectIsMessengerIntegration } from '../../../config/selectors';
import { selectDiagnosticsSectionActive } from '../../../diagnostics/selectors';
import { selectIsDiagnosticsEnabled } from '../../../flags/selectors';
import { selectIsVirtualBackgroundEnabled } from '../../../settings';
import { getUserSelector } from '../../../user';
import { SettingsSectionKey } from '../../constants';
import { selectIsHiddenSectionVisible, selectIsSettingsModalOpened } from '../../slice';

type SettingsSectionsHiddenMap = Partial<Record<SettingsSectionKey, boolean>>;

export function useSettingsSectionsHiddenMap(): SettingsSectionsHiddenMap {
    const isMessengerIntegration = useSelector(selectIsMessengerIntegration);
    const user = useSelector(getUserSelector);
    const isHiddenSectionVisible = useSelector(selectIsHiddenSectionVisible);
    const conferenceStatus = useSelector(getConferenceStatus);
    const isVirtualBackgroundEnabled = useSelector(selectIsVirtualBackgroundEnabled);
    const isPermissionsChangeAllowed = useSelector(isPermissionsChangeAllowedSelector);
    const isPermissionsExtendedActive = useSelector(selectIsPermissionsExtendedActive);
    const isPermissionsExtendedEnabled = useSelector(selectIsPermissionsExtendedEnabled);
    const isOpened = useSelector(selectIsSettingsModalOpened);
    const isDiagnosticsEnabled = useSelector(selectIsDiagnosticsEnabled);
    const isDiagnosticsActive = useSelector(selectDiagnosticsSectionActive);

    const [isPermissionsExtendedHidden, setIsPermissionsExtendedHidden] = useState(!isPermissionsExtendedActive);
    useLayoutEffect(() => {
        if (!isOpened || isPermissionsExtendedActive) {
            setIsPermissionsExtendedHidden(!isPermissionsExtendedActive);
        }
    }, [isPermissionsExtendedActive, isOpened]);

    const isDefaultPermissionsHidden =
        conferenceStatus !== ConferenceStatus.JOINED || !isPermissionsChangeAllowed || isPermissionsExtendedEnabled;
    const isDiagnosticsVisible = isDiagnosticsEnabled && isDiagnosticsActive;

    return useMemo(
        () => ({
            [SettingsSectionKey.VirtualBackground]: !isVirtualBackgroundEnabled || !supportsVirtualBackground(),
            [SettingsSectionKey.Account]: isMessengerIntegration,
            [SettingsSectionKey.Theme]: !user || isMessengerIntegration,
            [SettingsSectionKey.Permissions]: isDefaultPermissionsHidden && isPermissionsExtendedHidden,
            [SettingsSectionKey.Developers]: !isHiddenSectionVisible,
            [SettingsSectionKey.Diagnostics]: !isDiagnosticsVisible,
        }),
        [
            isVirtualBackgroundEnabled,
            isMessengerIntegration,
            user,
            isDefaultPermissionsHidden,
            isPermissionsExtendedHidden,
            isHiddenSectionVisible,
            isDiagnosticsVisible,
        ]
    );
}
